import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/media";

const DESTINATIONS = [
  { to: "/destinations/asia-dmc", label: "Asia DMC (Hub)" },
  { to: "/dubai-dmc", label: "Dubai" },
  { to: "/singapore-dmc", label: "Singapore" },
  { to: "/malaysia-dmc", label: "Malaysia" },
  { to: "/thailand-dmc", label: "Thailand" },
  { to: "/hong-kong-dmc", label: "Hong Kong" },
  { to: "/vietnam-dmc", label: "Vietnam" },
  { to: "/philippines-dmc", label: "Philippines" },
  { to: "/cambodia-dmc", label: "Cambodia" },
  { to: "/bali-dmc", label: "Bali (Indonesia)" },
  { to: "/laos-dmc", label: "Laos" },
  { to: "/japan-dmc", label: "Japan" },
];

const SERVICES = [
  { to: "/mice-services", label: "MICE Services" },
  { to: "/corporate-travel", label: "Corporate Travel" },
  { to: "/incentive-travel", label: "Incentive Travel" },
];

const SIMPLE_LINKS = [
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function DesktopDropdown({ label, items, testId }) {
  return (
    <div className="relative group" data-testid={`nav-${testId}-group`}>
      <button
        type="button"
        className="font-heading text-sm font-medium text-[#0B192C] hover:text-[#FF5238] transition-colors inline-flex items-center gap-1"
        data-testid={`nav-${testId}-trigger`}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
      </button>
      {/* Hover bridge to keep menu open */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-50">
        <ul
          className="min-w-[220px] bg-white border border-slate-200 shadow-xl py-2"
          data-testid={`nav-${testId}-menu`}
        >
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `block px-5 py-2.5 text-sm font-heading transition-colors ${
                    isActive
                      ? "text-[#FF5238] bg-[#FDFCF8]"
                      : "text-[#0B192C] hover:text-[#FF5238] hover:bg-[#FDFCF8]"
                  }`
                }
                data-testid={`nav-${testId}-item-${item.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MobileSection({ label, items }) {
  return (
    <div className="border-b border-slate-200 pb-4">
      <div className="overline mb-3">{label}</div>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="block font-heading text-base text-[#0B192C] hover:text-[#FF5238] transition-colors"
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      className="glass-header sticky top-0 z-50"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 lg:px-16 h-28">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-home-link">
          <img
            src={LOGO_URL}
            alt="Trawelldmc — DMC Dubai and Southeast Asia"
            width="220"
            height="56"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-12 md:h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          <DesktopDropdown label="Destinations" items={DESTINATIONS} testId="destinations" />
          <DesktopDropdown label="Services" items={SERVICES} testId="services" />
          {SIMPLE_LINKS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-heading text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#FF5238]"
                    : "text-[#0B192C] hover:text-[#FF5238]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="header-ai-quote-btn"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium font-heading text-[#0B192C] hover:text-[#FF5238] transition-colors"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-[#FF5238] animate-pulse" />
            AI Quote
          </Link>
          <Link to="/inquiry" data-testid="header-inquiry-cta" className="btn-primary text-sm !py-3 !px-5">
            Get Proposal
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 -mr-2 text-[#0B192C]"
            aria-label="Open menu"
            data-testid="mobile-menu-trigger"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60] bg-white overflow-y-auto"
          data-testid="mobile-menu-overlay"
        >
          <div className="flex items-center justify-between px-6 h-28 border-b border-slate-200">
            <img
              src={LOGO_URL}
              alt="Trawelldmc — DMC Dubai and Southeast Asia"
              width="220"
              height="56"
              loading="lazy"
              decoding="async"
              className="h-12 w-auto"
            />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="p-2 -mr-2 text-[#0B192C]"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav
            className="px-6 py-8 space-y-6"
            onClick={() => setMobileOpen(false)}
          >
            <MobileSection label="Destinations" items={DESTINATIONS} />
            <MobileSection label="Services" items={SERVICES} />
            <MobileSection label="Company" items={SIMPLE_LINKS} />
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/quote" className="btn-secondary justify-center">AI Quote</Link>
              <Link to="/inquiry" className="btn-primary justify-center">Get Proposal</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
